import { authenticatedFetch } from "@/api/session";
export type DashboardPriority = {
  id: string;
  title: string;
  description: string;
  recommendation: string;
  severity: "INFO" | "WATCH" | "WARNING" | "CRITICAL";
  entity_type: string;
  entity_id?: string | null;
  to: string;
};

export type DashboardBriefItem = {
  id: string;
  type: string;
  severity: "INFO" | "WATCH" | "WARNING" | "CRITICAL";
  message: string;
  to?: string | null;
};

export type DashboardEvent = {
  id: string;
  event_type: string;
  severity: string;
  title: string;
  description: string;
  recommendation?: string | null;
  member_id?: string | null;
  household_id?: string | null;
  is_read: boolean;
  created_at: string;
};

export type DashboardDateItem = {
  type: "BIRTHDAY" | "ANNIVERSARY";
  member_id?: string | null;
  household_id?: string | null;
  display_name: string;
  date: string;
  anniversary_years?: number | null;
};

export type DashboardTrendPoint = {
  label: string;
  value: number;
};

export type OnboardingTask = {
  key: string;
  title: string;
  description: string;
  to: string;
  completed: boolean;
};

export type OnboardingProgress = {
  tasks: OnboardingTask[];
  completed_task_count: number;
  total_task_count: number;
  next_recommended_action: OnboardingTask | null;
  maturity: "EMPTY" | "EARLY" | "ESTABLISHED";
  executive_analytics_ready: boolean;
};

export type DashboardResponse = {
  overall_attendance: { total_present: number; eligible_services: number; average_attendance: number; unique_attendees: number };
  church_name: string;
  onboarding: OnboardingProgress;
  church_health: number | null;
  unassessed_households: number;
  intelligence_run_id: string | null;
  church_health_status: string;

  attendance_rate: number;
  attendance_change: number;

  total_members: number;
  active_members: number;
  inactive_members: number;

  visitors: number;
  new_members_this_month: number;

  at_risk_members: number;
  critical_members: number;

  open_care_items: number;
  households_at_risk: number;

  attendance_records: number;
  attendance_trend_label: string;

  ministry_brief: DashboardBriefItem[];
  priorities: DashboardPriority[];
  recent_events: DashboardEvent[];
  milestones: DashboardDateItem[];

  attendance_trend: DashboardTrendPoint[];
  member_health_trend: DashboardTrendPoint[];
};

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  "http://localhost:8000";

function getAuthHeaders() {
  const token = localStorage.getItem("access_token");

  return {
    Authorization: `Bearer ${token}`,
  };
}

export async function getDashboard(
  churchId: string
): Promise<DashboardResponse | ScopedDashboardResponse> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/dashboard`,
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to load dashboard");
  }

  return response.json();
}

export async function getOnboardingProgress(
  churchId: string,
): Promise<OnboardingProgress> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/dashboard/onboarding`,
    { headers: getAuthHeaders() },
  );

  if (!response.ok) {
    throw new Error("Failed to load onboarding progress");
  }

  return response.json();
}

export interface ScopedDashboardResponse {
  limited: true;
  church_name: string;
  sections: { key: string; label: string; value: number | null; to: string }[];
}
